// src/components/sidebar/AlertsPage.jsx
import { useEffect, useState } from "react";
import { io } from "socket.io-client";
import "../../styles/sidebar/AlertsPage.css";
import { useMamdani } from "../../context/MamdaniContext";

const API_BASE = "https://api.anti-bot.ai";

const TYPE_CONFIG = {
  critical: { color: '#ff2244', bg: 'rgba(255,34,68,0.10)', border: 'rgba(255,34,68,0.30)', dot: '#ff2244', label: 'קריטי' },
  warning:  { color: '#ff8c00', bg: 'rgba(255,140,0,0.10)',  border: 'rgba(255,140,0,0.30)',  dot: '#ff8c00', label: 'פעיל'   },
  info:     { color: '#D89B5A', bg: 'rgba(216,155,90,0.08)', border: 'rgba(216,155,90,0.25)', dot: '#D89B5A', label: 'מידע'   },
};

function timeLabel(minutes) {
  if (!minutes || minutes < 1) return 'עכשיו';
  if (minutes < 60)  return `לפני ${minutes} דקות`;
  const h = Math.floor(minutes / 60);
  return `לפני ${h} שעות`;
}

function buildMamdaniAlerts(allBots) {
  return allBots.map((bot, idx) => {
    const type = bot.bot_type === 'BOT' ? 'critical'
               : bot.bot_type === 'LIKELY_BOT' ? 'warning' : 'info';
    const flags = [
      bot.is_coordinated        && 'COORDINATED',
      bot.anti_israel_count > 0 && `Anti-Israel: ${bot.anti_israel_count}`,
    ].filter(Boolean).join(' · ');
    const detectedDate = bot.first_seen_at
      ? new Date(bot.first_seen_at).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : null;
    return {
      id:      `mm-${idx}`,
      type,
      title:   `${bot.bot_type.replace(/_/g, ' ')} Detected`,
      message: `@${bot.username} — Score: ${bot.bot_score}/100${flags ? ` — ${flags}` : ''}`,
      source:  detectedDate ? `Mamdani Watchlist · Detected ${detectedDate}` : `Mamdani Watchlist`,
    };
  });
}

const VISIBLE = 5;

export default function AlertsPage() {
  const { mamdaniMode: mm, data: mmData } = useMamdani();
  const [alerts,  setAlerts]  = useState([]);
  const [summary, setSummary] = useState({ total: 0, critical: 0, active: 0 });
  const [filter,  setFilter]  = useState('all');
  const [loading, setLoading] = useState(true);
  const [offset,  setOffset]  = useState(0);
  const [animKey, setAnimKey] = useState(0);

  // מוד ממדאני — בנה התראות מבוטים
  useEffect(() => {
    if (!mm) return;
    const mmAlerts = buildMamdaniAlerts(mmData.allBots);
    setAlerts(mmAlerts);
    setSummary({
      total:    mmAlerts.length,
      critical: mmAlerts.filter(a => a.type === 'critical').length,
      active:   mmAlerts.filter(a => a.type === 'warning').length,
    });
    setLoading(false);
  }, [mm, mmData.allBots]);

  useEffect(() => {
    if (mm) return;
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 5000);
    const socket = io(API_BASE, { transports: ['polling', 'websocket'] });
    socket.on('dashboard_stats_update', fetchAlerts);
    socket.on('alerts_update', fetchAlerts);
    return () => { clearInterval(interval); socket.close(); };
  }, [mm]);

  // לולאה — כל 2.5 שניות מזיז אחת קדימה
  useEffect(() => {
    const loop = setInterval(() => {
      setOffset(prev => prev + 1);
      setAnimKey(prev => prev + 1);
    }, 2500);
    return () => clearInterval(loop);
  }, []);

  async function fetchAlerts() {
    try {
      const res  = await fetch(`${API_BASE}/api/alerts`);
      const data = await res.json();
      if (data.success) {
        setAlerts(data.alerts);
        setSummary(data.summary);
      }
    } catch (e) {
      console.error('שגיאה בטעינת התראות:', e);
    } finally {
      setLoading(false);
    }
  }

  const allFiltered = alerts.filter(a => {
    if (filter === 'all')      return true;
    if (filter === 'critical') return a.type === 'critical';
    if (filter === 'warning')  return a.type === 'warning';
    return true;
  });

  const filtered = allFiltered.length > 0
    ? Array.from({ length: VISIBLE }, (_, i) =>
        allFiltered[(offset + i) % allFiltered.length]
      )
    : [];

  return (
    <div className="ap-page" dir={mm ? "ltr" : "rtl"}>

      {/* ── כותרת ── */}
      <div className="ap-header">
        <div className="ap-header-right">
          <h1 className="ap-title">{mm ? "Bot Alerts" : "התראות"}</h1>
          <p className="ap-subtitle">{mm ? "Mamdani Watchlist — Detected Bots" : "התראות ואירועים אחרונים"}</p>
        </div>
        <div className="ap-badges">
          <span className="ap-badge critical">{summary.critical} {mm ? "Critical" : "קריטיות"}</span>
          <span className="ap-badge warning">{summary.active} {mm ? "Warning" : "פעילות"}</span>
        </div>
      </div>

      {/* ── סרגל פעולות ── */}
      <div className="ap-toolbar">
        <div className="ap-filters">
          {(mm
            ? [{ id: 'all', label: 'All' }, { id: 'critical', label: 'Critical' }, { id: 'warning', label: 'Warning' }]
            : [{ id: 'all', label: 'הכל' }, { id: 'critical', label: 'קריטיות' }, { id: 'warning', label: 'פעילות' }]
          ).map(f => (
            <button
              key={f.id}
              className={`ap-filter ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── רשימת התראות ── */}
      <div className="ap-list">
        {loading && (
          <div className="ap-empty">{mm ? "Loading alerts..." : "טוען התראות..."}</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="ap-empty">
            {mm ? 'No active alerts' : 'אין התראות פעילות'}
          </div>
        )}

        {filtered.map((alert, i) => {
          const cfg   = TYPE_CONFIG[alert.type] || TYPE_CONFIG.info;
          const isNew = i === VISIBLE - 1;

          return (
            <div
              key={isNew ? `new-${animKey}` : alert.id}
              className="ap-alert"
              style={{ background: cfg.bg, borderColor: cfg.border }}
            >
              <div className="ap-alert-body">
                <div className="ap-alert-top">
                  <span className="ap-dot" style={{ background: cfg.dot }} />
                  <span className="ap-alert-title">{alert.title}</span>
                </div>
                <div className="ap-alert-message">{alert.message}</div>
                <div className="ap-alert-meta">
                  <span className="ap-alert-source">{alert.source}</span>
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}